import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { EsignatureConfigUpdateStore } from "../../store";
import { EsignatureProvider } from "../../../../api/graphql";
import { InputReference } from "../../../common/controls";
import { InputCodeEditor } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { InputString } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type EsignatureConfigUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  providerOptions: { id: string; displayId: string }[];
  providerError?: string;
  provider: EsignatureProvider;
  onProviderChange?: (value: EsignatureProvider) => void;
  credentials: string;
  credentialsSelectedValue?: { displayId: string; id: string } | null;
  onCredentialsChange?: (value: string) => void;
  credentialsError?: string;
  sandboxMode: boolean;
  sandboxModeSelectedValue?: { displayId: string; id: string } | null;
  onSandboxModeChange?: (value: boolean) => void;
  sandboxModeError?: string;
  webhookSecret: string;
  webhookSecretSelectedValue?: { displayId: string; id: string } | null;
  onWebhookSecretChange?: (value: string) => void;
  webhookSecretError?: string;
  enabled: boolean;
  enabledSelectedValue?: { displayId: string; id: string } | null;
  onEnabledChange?: (value: boolean) => void;
  enabledError?: string;
  esignatureConfigUpdateStore: EsignatureConfigUpdateStore;
};

export const EsignatureConfigUpdateForm: React.FC<EsignatureConfigUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      providerOptions = [],
      providerError,
      provider = "" as EsignatureProvider,
      onProviderChange = () => {},
      credentials = "{}",
      credentialsError,
      onCredentialsChange = () => {},
      sandboxMode = false,
      sandboxModeError,
      onSandboxModeChange = () => {},
      webhookSecret = "",
      webhookSecretError,
      onWebhookSecretChange = () => {},
      enabled = false,
      enabledError,
      onEnabledChange = () => {},
      esignatureConfigUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="EsignatureConfig"
              mode="update"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "provider":
                    onProviderChange?.(valor);
                    break;
                  case "credentials":
                    onCredentialsChange?.(valor);
                    break;
                  case "sandboxMode":
                    onSandboxModeChange?.(valor);
                    break;
                  case "webhookSecret":
                    onWebhookSecretChange?.(valor);
                    break;
                  case "enabled":
                    onEnabledChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={esignatureConfigUpdateStore.getCardErrors("null")}
              title={langStore.groupEsignatureConfigLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputReference
                  id="provider"
                  autoFocus
                  disabled={false}
                  error={Boolean(providerError)}
                  helperText={
                    providerError ||
                    esignatureConfigUpdateStore.warnings["provider"]?.message
                  }
                  label={langStore.EsignatureConfigProviderLabel}
                  options={providerOptions}
                  selectedValue={
                    provider
                      ? {
                          id: provider,
                          displayId:
                            providerOptions.find(
                              (o: { id: string }) => o.id === provider
                            )?.displayId || provider,
                        }
                      : null
                  }
                  onChange={() => {}}
                  onSelect={(value: { id: string; displayId: string } | null) =>
                    onProviderChange(
                      (value?.id as EsignatureProvider) ||
                        ("" as EsignatureProvider)
                    )
                  }
                />
                <InputCodeEditor
                  value={credentials}
                  error={Boolean(credentialsError)}
                  helperText={
                    credentialsError ||
                    esignatureConfigUpdateStore.warnings["credentials"]?.message
                  }
                  label={langStore.EsignatureConfigCredentialsLabel}
                  onChange={(value: string) => onCredentialsChange(value)}
                  language="json"
                  description={langStore.EsignatureConfigCredentialsDescription}
                />
                <InputBoolean
                  value={sandboxMode}
                  error={Boolean(sandboxModeError)}
                  helperText={
                    sandboxModeError ||
                    esignatureConfigUpdateStore.warnings["sandboxMode"]?.message
                  }
                  label={langStore.EsignatureConfigSandboxModeLabel}
                  onChange={(value: boolean) => onSandboxModeChange(value)}
                />
                <InputString
                  value={webhookSecret}
                  error={Boolean(webhookSecretError)}
                  warning={
                    !webhookSecretError &&
                    Boolean(
                      esignatureConfigUpdateStore.warnings["webhookSecret"]
                    )
                  }
                  helperText={
                    webhookSecretError ||
                    esignatureConfigUpdateStore.warnings["webhookSecret"]
                      ?.message
                  }
                  label={langStore.EsignatureConfigWebhookSecretLabel}
                  onChange={(value: string) => onWebhookSecretChange(value)}
                  nullText={langStore.optionalFieldHint}
                />
                <InputBoolean
                  value={enabled}
                  error={Boolean(enabledError)}
                  helperText={
                    enabledError ||
                    esignatureConfigUpdateStore.warnings["enabled"]?.message
                  }
                  label={langStore.EsignatureConfigEnabledLabel}
                  onChange={(value: boolean) => onEnabledChange(value)}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
